import { Box, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { BookingResourceInfo } from "../../features/Schedule/typings/booking-resource-info.types";
import { resourceKindLabel } from "../../features/Schedule/utils/resource-booking-schedule.utils";
import { InfoRow } from "./vehicle-information-card-contents.component";

type ResourceInformationCardContentsProps = Readonly<{
  resourceInfo: BookingResourceInfo;
  trailingDetail?: React.ReactNode;
}>;

function resolveResourceCardDescription(
  resourceInfo: BookingResourceInfo,
  kindLabel: string
): string | null {
  const description = resourceInfo.description?.trim();
  if (!description) {
    return null;
  }

  const isSameAs = (other: string) =>
    description.localeCompare(other.trim(), undefined, { sensitivity: "accent" }) === 0;

  if (isSameAs(kindLabel) || isSameAs(resourceInfo.name)) {
    return null;
  }

  return description;
}

export function ResourceInformationCardContents({
  resourceInfo,
  trailingDetail
}: ResourceInformationCardContentsProps) {
  const { t } = useTranslation();
  const kindLabel = resourceKindLabel(resourceInfo.resourceKind, t);
  const description = resolveResourceCardDescription(resourceInfo, kindLabel);

  return (
    <Stack spacing={2} data-testid="resource-information-card-contents">
      <Box>
        <Typography variant="h5" fontWeight={600}>
          {resourceInfo.name}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {kindLabel}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {description}
          </Typography>
        ) : null}
      </Box>

      <Divider sx={{ mt: 3, mb: 3, opacity: "0.6" }} />

      <Stack spacing={1}>
        {resourceInfo.locationName ? (
          <InfoRow label={t("Location")} value={resourceInfo.locationName} />
        ) : null}
        {resourceInfo.categoryName ? (
          <InfoRow label={t("Category")} value={resourceInfo.categoryName} />
        ) : null}
        {resourceInfo.bookingLeadTime != null ? (
          <InfoRow
            label={t("_equipmentManagement_bookingLeadTime")}
            value={`${resourceInfo.bookingLeadTime} h`}
          />
        ) : null}
        {resourceInfo.pricePerUnit != null ? (
          <InfoRow
            label={resourceInfo.resourceKind === "catering" ? t("Price/Unit") : t("Price (h)")}
            value={resourceInfo.pricePerUnit}
          />
        ) : null}
        {trailingDetail}
      </Stack>
    </Stack>
  );
}
